import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Auth0WrapperService } from '@auth/bp-pf-auth';
import { DropdownModule } from 'primeng/dropdown';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { HeaderConfig } from '../../core/data/interface/header.interface';
import { filter, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CommonService } from '../../core/common/common.service';
import { UserRole } from '../../core/data/model/user-role.model';
import { RoleService } from '../../core/others/role/role.service';
import { DEFAULT_ROUTE_BY_ROLE } from '../../core/data/config/user-role.config';
import { NotificationComponent } from '@notification/bp-pf-notification';
import { TooltipModule } from 'primeng/tooltip';
import { SupportComponent } from '@support/bp-pf-support';



@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownModule, NotificationComponent, TooltipModule, SupportComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {

  @Input() showBack = false;
  @Input() isSidebarOpen = false;
  @Input() hasSidebar = true;

  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() actionClicked = new EventEmitter<{ action: any, context?: any }>();

  @Output() toggleSupport = new EventEmitter<void>();


  config?: HeaderConfig;
  role!: UserRole;

  title = '';
  subtitle = '';
  userName = '';

  maxSubtitleLength = 70;

  appId: string = environment.app_id;
  isPanelOpen = false
  // Cleanup notifier for subscriptions
  private readonly destroy$ = new Subject<void>();

  private resizeObserver!: ResizeObserver;
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private commonService: CommonService,
    private roleService: RoleService,
    private el: ElementRef,
  ) { }

  ngOnInit(): void {

    this.initHeader();
    this.initRoleListener();
    this.initResizeHandler();

    this.commonService.header$
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        if (config) {
          this.config = config;
          this.title = config.title || '';
          this.subtitle = config.subtitle || '';
        }
      });
  }

  private initHeader(): void {
    this.setHeaderFromRoute();
    this.userNameVisibility();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setHeaderFromRoute();
        this.userNameVisibility();
      });
  }

  private userNameVisibility(): void {
    if (this.config?.showUserName) {
      this.loadUser();
    } else {
      this.userName = '';
    }
  }

  private initRoleListener(): void {
    this.roleService.role$
      .pipe(takeUntil(this.destroy$))
      .subscribe(role => {
        this.role = role;
      });
  }

  private initResizeHandler(): void {
    this.updateMaxLength(window.innerWidth);

    window.addEventListener('resize', this.onResize);
  }

  private onResize = () => {
    this.updateMaxLength(window.innerWidth);
  };



  loadUser() {
    const email = this.commonService.getEmail();
    if (email) {
      const parts = email.split('@')[0].split('.');
      this.userName = parts
        .map(p => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    }
  }

  updateMaxLength(width: number) {
    if (width > 1200) this.maxSubtitleLength = 100;
    else if (width > 992) this.maxSubtitleLength = 60;
    else if (width > 768) this.maxSubtitleLength = 45;
    else this.maxSubtitleLength = 30;
  }

  /*-------Helpers------- */

  get displayTitle(): string {
    if (this.config?.showUserName && this.userName) {
      return `${this.title}, ${this.userName}`;
    }
    return this.title;
  }


  get truncatedSubtitle(): string {
    if (!this.subtitle) return '';
    return this.subtitle.length > this.maxSubtitleLength
      ? this.subtitle.slice(0, this.maxSubtitleLength) + '...'
      : this.subtitle;
  }

  switchRole(role: UserRole) {
    this.roleService.setRole(role);
    const defaultRoute = DEFAULT_ROUTE_BY_ROLE[role];
    this.commonService.navigateToURLWithtenantCode(defaultRoute);
  }


  private setHeaderFromRoute(): void {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    this.config = route.snapshot.data['header'];

    this.title = this.config?.title || '';
    this.subtitle = this.config?.subtitle || '';
  }


  goBack(): void {
    const firstCrumb = this.config?.breadcrumbs?.[0];
    if (firstCrumb?.route) {
      this.commonService.navigateToURLWithtenantCode(firstCrumb.route);
      return;
    }

    // fallback if no breadcrumb route exists
    if (this.role === 'agent') {
      this.commonService.navigateToURLWithtenantCode('dashboard');
    } else {
      this.commonService.navigateToURLWithtenantCode('work-view');
    }
  }


  onActionClick(action: any): void {
    if (action.disabled) return;
    // Handle UI actions
    if (action.action) {
      this.actionClicked.emit({
        action: action.action,
        context: action.context
      });
      return;
    }

    if (action.tenantRoute) {
      this.commonService.navigateToURLWithtenantCode(action.tenantRoute);
      return;
    }

    // Handle routing actions
    if (Array.isArray(action.route)) {
      const isAbsoluteRoute = action.route[0]?.startsWith?.('/');
      this.router.navigate(
        action.route,
        isAbsoluteRoute ? {} : { relativeTo: this.getDeepestRoute() }
      );
      return;
    }
  }

  private getDeepestRoute(): ActivatedRoute {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }


  navigate(route: string) {
    this.commonService.navigateToURLWithtenantCode(route);
  }

  // ngAfterViewInit() {
  //   const header = this.el.nativeElement.querySelector('.app-header');
  //   this.resizeObserver = new ResizeObserver(entries => {
  //     for (const entry of entries) {
  //       const h = entry.contentRect.height;
  //       document.documentElement.style.setProperty('--header-height', `${h}px`);
  //     }
  //   });
  //   this.resizeObserver?.observe(header);
  // }
  ngAfterViewInit() {
    const header = this.el.nativeElement.querySelector('.app-header');

    if (!header) {
      return;
    }

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      }
    });

    this.resizeObserver.observe(header);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  openSupport() {
    this.toggleSupport.emit();
  }
}
